import {
  createContext,
  ReactNode,
  useContext,
  useState,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { API_BASE_URL } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";

// Define the User type
interface User {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: "customer" | "admin";
  streetAddress?: string;
  fullName?: string;
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (userData: RegisterData) => Promise<{ message: string }>;
  logout: () => Promise<void>;
  error: string | null;
  verifyRecentPassword: (password: string) => Promise<boolean>;
  checkSensitiveAuthStatus: () => Promise<boolean>;
}

type LoginResult =
  | { ok: true }
  | { ok: false; code?: string; email?: string; message: string };

type AuthError = Error & {
  code?: string;
  email?: string;
};

// Registration data interface
interface RegisterData {
  email: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Login data interface
interface LoginData {
  identifier: string; // Can be username or email
  password: string;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // User query - fetches the current user data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch,
  } = useQuery({
    queryKey: [`${API_BASE_URL}/api/auth/me`],
    queryFn: async () => {
      // If we don't have a token, don't fetch
      const token = localStorage.getItem("token");
      if (!token) return null;

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // If we get a 401, clear the token
          if (response.status === 401) {
            localStorage.removeItem("token");
            return null;
          }
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();
        return data.user; // Return user from data.user according to API spec
      } catch {
        console.error("Error fetching user:", error);
        return null;
      }
    },
    retry: false,
  });

  const verifyPasswordMutation = useMutation({
    mutationFn: async (password: string) => {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-recent`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Password verification failed");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Refresh user data to get updated sensitive auth status
      refetch();
      toast({
        title: "Authentication successful",
        description: "You can now access sensitive information.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Authentication failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Login mutation - handles user login
  const loginMutation = useMutation({
    mutationFn: async (data: LoginData) => {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const authError = new Error(errorData.message || "Login failed") as AuthError;
        authError.code = errorData.code;
        authError.email = errorData.email;
        throw authError;
      }
      return await response.json();
    },
    onSuccess: async (data) => {
      if (!data || !data.token) return;
      // Store the token in localStorage
      localStorage.setItem("token", data.token);
      // Store user id in localstorage
      localStorage.setItem("userID", data.user.id);

      const currentTime = Date.now();
      localStorage.setItem("sensitiveAuthTime", currentTime.toString());

      // Update the user in the query cache
      queryClient.setQueryData([`${API_BASE_URL}/api/auth/me`], data?.user);
      
      // Force refetch to ensure we have the latest user data with role
      await queryClient.invalidateQueries({
        queryKey: [`${API_BASE_URL}/api/auth/me`],
      });
      
      // toast({
      //   title: "Login successful",
      //   description: `Welcome back, ${data?.user.username}!`,
      // });
    },
    onError: (error: AuthError) => {
      setError(error.message);

      if (error.code === "EMAIL_NOT_VERIFIED") {
        toast({
          title: "Verify your email",
          description: error.message,
        });
        return;
      }

      toast({
        title: "Login failed",
        description: error?.message,
        variant: "destructive",
      });
    },
  });

  // Register mutation - handles user registration
  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      const recaptchaToken = await getRecaptchaToken('register');

      // Prepare the data according to the API spec
      // Include fullName for backward compatibility with validation middleware
      const registerData = {
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: `${data.firstName} ${data.lastName}`.trim(),
        username: data.username,
        email: data.email,
        password: data.password,
        recaptchaToken,
      };

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(recaptchaToken ? { "x-recaptcha-token": recaptchaToken } : {}),
        },
        body: JSON.stringify(registerData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Registration failed");
      }

      return await response.json();
    },
    onSuccess: (data) => {
      if (data.message) {
        toast({
          title: "Registration Successful",
          description: data.message,
        });
      }
    },
    onError: (error: Error) => {
      setError(error.message);
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Logout mutation - handles user logout
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Logout failed");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Remove the token from localStorage
      localStorage.removeItem("token");
      // Remove the User id from localStorage
      localStorage.removeItem("userID");
      // Clear the user from the query cache
      queryClient.setQueryData([`${API_BASE_URL}/api/auth/me`], null);
      // Invalidate queries that might depend on the user's authentication status
      queryClient.invalidateQueries({
        queryKey: [`${API_BASE_URL}/api/auth/me`],
      });
      toast({
        title: "Logout successful",
        description: "You have been logged out.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // check sensitive auth status from server
  const checkSensitiveAuthStatus = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;
      
      const response = await fetch(`${API_BASE_URL}/api/auth/check-sensitive`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.ok;
    } catch {
      return false;
    }
  };

  // Server-side password verification
  const verifyRecentPassword = async (password: string): Promise<boolean> => {
    try {
      await verifyPasswordMutation.mutateAsync(password);
      return true;
    } catch {
      return false;
    }
  };

  // Handle login function
  const login = async (email: string, password: string): Promise<LoginResult> => {
    setError(null);

    try {
      await loginMutation.mutateAsync({ identifier: email, password });
      return { ok: true };
    } catch (error) {
      const authError = error as AuthError;
      if (authError.code === "EMAIL_NOT_VERIFIED") {
        return {
          ok: false,
          code: authError.code,
          email: authError.email,
          message: authError.message,
        };
      }

      throw error;
    }
  };

  // Handle register function
  const register = async (
    userData: RegisterData
  ): Promise<{ message: string }> => {
    setError(null);
    return await registerMutation.mutateAsync(userData);
  };

  // Handle logout function
  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const isLoading =
    isUserLoading ||
    loginMutation.isPending ||
    registerMutation.isPending ||
    logoutMutation.isPending ||
    verifyPasswordMutation.isPending;

  // The value to be provided to consumers of this context
  const authContext: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    error,
    verifyRecentPassword,
    checkSensitiveAuthStatus,
  };

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
}

// Custom hook for using the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
